// Shared by the Estimates page and the EstimatePanel on a technical view:
// one API helper, one number format and one preview, so the two places can
// never show an estimate differently.
import React from "react";
import { Alert, Descriptions, Table } from "antd";
import { url, tokenRef } from "api";

export const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });
export async function api(path, options = {}) {
  const r = await fetch(`${url}${path}`, { ...options, headers: { ...auth(), "Content-Type": "application/json", ...(options.headers || {}) } });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.err || `HTTP ${r.status}`);
  return body;
}
export async function download(path, fallbackName) {
  const r = await fetch(`${url}${path}`, { headers: auth() });
  if (!r.ok) { const b = await r.json().catch(() => ({})); throw new Error(b.err || `HTTP ${r.status}`); }
  const blob = await r.blob();
  const name = (((r.headers.get("Content-Disposition") || "").match(/filename="([^"]+)"/) || [])[1]) || fallbackName;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

export const money = (n) => (n === null || n === undefined ? "-" : `$${Number(n).toLocaleString("en-US", { minimumFractionDigits: Math.round(n * 100) % 100 ? 2 : 0, maximumFractionDigits: 2 })}`);
export const hrs = (n) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 1 })} h`;

export function PreviewPanel({ r }) {
  const h = r.hours || {}; const c = r.coordination || {};
  return (
    <div style={{ marginTop: 16, borderTop: "1px solid #eee", paddingTop: 12 }}>
      <Descriptions size="small" column={{ xs: 1, md: 3 }} bordered items={[
        { key: "p", label: "Production", children: hrs(h.production) },
        { key: "a", label: "Allowances", children: hrs(h.allowances) },
        { key: "m", label: "Modelling total", children: hrs(h.modeling) },
        { key: "c", label: "Coordination", children: `${hrs(c.hours && c.hours.total)} (${c.totalIssues} issues over ${(c.rounds || []).length} rounds)` },
        { key: "b", label: "Blended rate", children: money(r.cost && r.cost.blendedRate) },
        { key: "w", label: "Window extension", children: r.windowExtension ? `${money(r.windowExtension.perWeek)} / week` : "-" },
      ]} />
      {(h.skipped || []).length ? <Alert style={{ marginTop: 8 }} type="info" showIcon message={`Not carried: ${h.skipped.map((s) => `${s.key} (${s.hours} h) - ${s.reason}`).join("; ")}`} /> : null}
      {(h.unrated || []).length ? <Alert style={{ marginTop: 8 }} type="warning" showIcon message={`Not priced, no production rate: ${h.unrated.map((u) => `${u.qty} x ${u.rateKey}`).join(", ")}`} /> : null}
      {r.cost && r.cost.unpriced ? <Alert style={{ marginTop: 8 }} type="warning" showIcon message="Some hours have no role on the rate card and are unpriced. Set the rate card above (pricing rights needed)." /> : null}
      <Table size="small" style={{ marginTop: 8 }} pagination={false} rowKey="key" dataSource={r.options || []} columns={[
        { title: "Option", dataIndex: "label" }, { title: "Hours", dataIndex: "hours", render: hrs },
        { title: "Fee", dataIndex: "fee", render: money }, { title: "Calculated", dataIndex: "calculated", render: money },
      ]} />
      {r.target ? (
        <Alert style={{ marginTop: 8 }} type={r.target.unreachable ? "warning" : "info"} showIcon
               message={r.target.closes && r.target.closes.length
                 ? `To reach ${money(r.target.target)}: ${r.target.closes.map((x) => x.label).join(" + ")} (${money(r.target.newFee)}). ${r.target.note}`
                 : r.target.note} />
      ) : null}
      <div style={{ marginTop: 8, color: "#666", fontSize: 12 }}>
        Confidence: {Object.entries((r.confidence && r.confidence.mix) || {}).filter(([, m]) => m.qty).map(([k, m]) => `${m.qty} ${k} (${m.share}%)`).join(" · ") || "-"}
      </div>
    </div>
  );
}

